import type { CalendarEventsChoice } from '@Integrations/components/calendar/CalendarEventsChoiceDialog';

type LinkData = Diagonal.Integrations.Data.GoogleCalendar.ExternalCalendarData;
type LoginData = Diagonal.Integrations.Data.GoogleCalendar.GoogleCalendarLoginData;
type StaffData = Diagonal.Integrations.Data.GoogleCalendar.CalendarStaffRecordData;

/** The fields of a link that the calendar surfaces show. The Page SDK's `SdkCalendarLink` has them too. */
export type CalendarLink = Pick<
  LinkData,
  | 'id'
  | 'calendar_name'
  | 'login_id'
  | 'account'
  | 'login_status'
  | 'viewer_owns_login'
  | 'connected_by_name'
  | 'receives_events'
  | 'blocks_availability'
  | 'state'
  | 'message'
  | 'checking'
  | 'busy_refreshed_at'
  | 'busy_stale'
>;

export type CalendarLogin = Pick<LoginData, 'id' | 'account' | 'status' | 'connected_at'>;

export type CalendarStaff = Pick<StaffData, 'resource_id' | 'record_id' | 'name' | 'is_viewer'>;

export type CalendarChoices = Diagonal.Integrations.Data.GoogleCalendar.CalendarChoicesData;

export type CalendarLinkFlags = {
  receivesEvents: boolean;
  blocksAvailability: boolean;
};

export type CalendarLinkInput = CalendarLinkFlags & { loginId: string } & (
    | { calendarId: string; email?: undefined; newCalendar?: undefined }
    | { email: string; calendarId?: undefined; newCalendar?: undefined }
    | { newCalendar: true; calendarId?: undefined; email?: undefined }
  );

/**
 * The requests behind the calendar surfaces. The admin page sends them as Inertia visits and the
 * Page SDK component through the calendar connections API, so both keep one layout and one set
 * of rules. Each rejects with the refusal, which `errorMessage` turns into text.
 */
export interface CalendarConnectionActions {
  connect: () => Promise<unknown>;
  reconnect: (loginId: string) => Promise<unknown>;
  disconnect: (loginId: string, events: CalendarEventsChoice) => Promise<unknown>;
  listChoices: () => Promise<CalendarChoices>;
  addLink: (input: CalendarLinkInput) => Promise<unknown>;
  updateLink: (
    linkId: string,
    flags: CalendarLinkFlags,
    events?: CalendarEventsChoice
  ) => Promise<unknown>;
  removeLink: (linkId: string, events: CalendarEventsChoice) => Promise<unknown>;
  errorMessage: (failure: unknown, fallback: string) => string;
}

/**
 * The explanation under a link, or `null` when its badge says enough: a disconnecting account
 * ("Removing events…") and an access check after a reconnect ("Checking…"). Where the page
 * already leads with a reconnect notice for the viewer's account, the row repeats nothing.
 */
export function calendarLinkMessage(
  link: Pick<CalendarLink, 'login_status' | 'checking' | 'message' | 'viewer_owns_login'>,
  { reconnectNoticeShown = false }: { reconnectNoticeShown?: boolean } = {}
): string | null {
  if (link.login_status === 'disconnecting' || link.checking) {
    return null;
  }

  if (
    reconnectNoticeShown &&
    link.viewer_owns_login &&
    link.login_status === 'reconnect_required'
  ) {
    return null;
  }

  return link.message;
}

/** A link that needs someone to act: not active, or blocking with stale busy time. */
export function calendarLinkNeedsAttention(
  link: Pick<CalendarLink, 'state' | 'checking' | 'login_status' | 'busy_stale'>
): boolean {
  if (link.checking || link.login_status === 'disconnecting') {
    return false;
  }

  return link.state !== 'active' || link.busy_stale;
}

/** A disconnect or an access check is still running, so the surface refreshes until it ends. */
export function calendarWorkPending(
  links: Pick<CalendarLink, 'login_status' | 'checking'>[],
  logins: Pick<CalendarLogin, 'status'>[]
): boolean {
  return (
    links.some((link) => link.checking || link.login_status === 'disconnecting') ||
    logins.some((login) => login.status === 'disconnecting')
  );
}

/** How often a surface refreshes while `calendarWorkPending` holds. */
export const CALENDAR_PENDING_POLL_MS = 5000;

/** How long a queued sync retry refreshes the page before it stops. */
export const CALENDAR_RETRY_REFRESH_LIMIT_MS = 2 * 60_000;
